import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useEditor } from "../editor/react/hooks";
import { SketchOutline } from "../ui/kit";

interface Item {
  label: string;
  hint?: string;
  run: () => void;
  danger?: boolean;
}

/** Long-press (touch) or right-click menu. */
export function ContextMenu({ onPasteRequest }: { onPasteRequest: (at: { x: number; y: number }) => void }) {
  const ed = useEditor();
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const vp = ed.viewport;
    if (!vp) return;
    const open = (e: Event) => {
      const d = (e as CustomEvent).detail as { x: number; y: number };
      const r = vp.getBoundingClientRect();
      setAt({ x: d.x + r.left, y: d.y + r.top });
    };
    vp.addEventListener("sepia:contextmenu", open);
    return () => vp.removeEventListener("sepia:contextmenu", open);
  }, [ed, ed.viewport]);

  useEffect(() => {
    if (!at) return;
    const close = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setAt(null);
    };
    const key = (e: KeyboardEvent) => e.key === "Escape" && setAt(null);
    window.addEventListener("pointerdown", close, true);
    window.addEventListener("keydown", key);
    return () => {
      window.removeEventListener("pointerdown", close, true);
      window.removeEventListener("keydown", key);
    };
  }, [at]);

  if (!at) return null;
  const sel = ed.selected();
  const ro = ed.ui.readOnly;
  const items: (Item | null)[] = sel.length
    ? [
        ro ? null : { label: "Cut", hint: "⌘X", run: () => ed.cut() },
        { label: "Copy", hint: "⌘C", run: () => ed.copy() },
        ro ? null : { label: "Duplicate", hint: "⌘D", run: () => ed.duplicateSelected() },
        null,
        ro ? null : { label: "Bring to front", hint: "⇧⌘]", run: () => ed.reorder("front") },
        ro ? null : { label: "Send to back", hint: "⇧⌘[", run: () => ed.reorder("back") },
        ro || sel.length < 2 ? null : { label: "Group", hint: "⌘G", run: () => ed.group() },
        ro || !sel.some((e) => e.groupId) ? null : { label: "Ungroup", hint: "⇧⌘G", run: () => ed.ungroup() },
        ro ? null : { label: sel.every((e) => e.locked) ? "Unlock" : "Lock", hint: "⇧⌘L", run: () => ed.toggleLock() },
        { label: "Zoom to selection", hint: "⇧2", run: () => ed.zoomToSelection() },
        null,
        ro ? null : { label: "Delete", hint: "⌫", run: () => ed.deleteSelected(), danger: true }
      ]
    : [
        ro ? null : { label: "Paste", hint: "⌘V", run: () => onPasteRequest(at) },
        { label: "Select all", hint: "⌘A", run: () => ed.selectAll() },
        { label: "Fit everything", hint: "⇧1", run: () => ed.zoomToFit() }
      ];
  const clean = items.filter((x, i, arr) => x || (i > 0 && arr[i - 1] && arr.slice(i + 1).some(Boolean)));
  const x = Math.min(at.x, window.innerWidth - 220);
  const y = Math.min(at.y, window.innerHeight - clean.length * 38 - 20);
  return createPortal(
    <div ref={ref} className="popover context-menu" style={{ left: x, top: y }} data-canvas-ignore>
      <SketchOutline seed="ctx" />
      <div className="menu" role="menu">
        {clean.map((it, i) =>
          it ? (
            <button
              key={i}
              type="button"
              role="menuitem"
              className={"menu-item" + (it.danger ? " danger" : "")}
              onClick={() => {
                setAt(null);
                it.run();
              }}
            >
              <span>{it.label}</span>
              {it.hint && <kbd>{it.hint}</kbd>}
            </button>
          ) : (
            <hr key={i} />
          )
        )}
      </div>
    </div>,
    document.body
  );
}
